import React from 'react';

import { Box, Typography, Button, Dialog, styled } from '@mui/material';

import { Icon } from '@liscio/ui';

import { 
  ExportErrorDetails, 
  ExportProgressDetails, 
  ExportSuccessDetails, 
  ExportLoadingState 
} from './components';
import { ExportStatus } from './types';

const CONSTANTS = {
  MAX_POLL_ATTEMPTS: 10,
  DIALOG_SHADOW: '0px 4px 12px rgba(0, 0, 0, 0.1)',
} as const;

// Styled Components
const DialogHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 3,
});

interface GruntWorxExportStatusDialogProps {
  open: boolean;
  exportStatus: ExportStatus | null;
  onClose: () => void;
  onRetry?: () => void;
  pollAttempts?: number;
  maxPollAttempts?: number;
  isLoading?: boolean;
}

export const GruntWorxExportStatusDialog = ({
  open,
  exportStatus,
  onClose,
  onRetry,
  pollAttempts = 0,
  maxPollAttempts = CONSTANTS.MAX_POLL_ATTEMPTS,
  isLoading = false,
}: GruntWorxExportStatusDialogProps) => {
  const isSuccess = exportStatus?.status === 'completed' && exportStatus?.success !== false;
  const isError = exportStatus?.status === 'failed' || exportStatus?.success === false;
  const isPending = exportStatus?.status === 'pending';
  const isProcessing = exportStatus?.status === 'processing';



  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          p: 0,
          bgcolor: '#FFFFFF',
          boxShadow: CONSTANTS.DIALOG_SHADOW,
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <DialogHeader>
          <Typography variant="h6">Export to GruntWorx</Typography>
          <Icon 
            icon="close" 
            onClick={onClose} 
            sx={{ cursor: 'pointer', color: 'text.secondary' }}
            data-testid="close-icon"
          />
        </DialogHeader>

        {isLoading ? (
          <ExportLoadingState />
        ) : (
          <>
            {isError && exportStatus && <ExportErrorDetails exportStatus={exportStatus} />}
            {(isPending || isProcessing) && exportStatus && (
              <ExportProgressDetails 
                exportStatus={exportStatus}
                pollAttempts={pollAttempts}
                maxPollAttempts={maxPollAttempts}
              />
            )}
            {isSuccess && exportStatus && <ExportSuccessDetails exportStatus={exportStatus} />}
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
          {isError && onRetry ? (
            <>
              <Button 
                variant="outlined" 
                onClick={onClose}
                sx={{
                  borderColor: 'divider',
                  color: 'text.primary'
                }}
              >
                Close
              </Button>
              <Button 
                variant="contained" 
                onClick={onRetry}
                sx={{
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                Retry Export
              </Button>
            </>
          ) : (
            <Button 
              variant="outlined" 
              onClick={onClose}
              sx={{
                borderColor: 'divider',
                color: 'text.primary'
              }}
            >
              Close
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}; 